import React from 'react';

import { AiFillFire } from 'react-icons/ai';
import { FaVoteYea } from 'react-icons/fa';
import { IoPieChartSharp } from 'react-icons/io5';

import { Avatar, AvatarGroup, Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { CompanyAnalysisFieldResponse, CompanyAnalysisUser } from '../types';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';

interface ResponseCardProps {
  response: CompanyAnalysisFieldResponse;
  isTopChoice: boolean;
  maxUsers?: number;
  infoUsers: CompanyAnalysisUser[];
}

const ResponseCard: React.FC<ResponseCardProps> = ({
  response,
  isTopChoice,
  maxUsers = 3,
  infoUsers,
}) => {
  const { value, total_users, percentage, users, badges } = response;
  const theme = useThemeContext();
  const hasTopChoiceBadge = badges.includes('TOP_CHOICE');
  const shouldHighlight = isTopChoice || hasTopChoiceBadge;

  const getUserPhotoUrl = (userId: string): string => {
    const user = infoUsers.find(u => u.user_uuid === userId);
    return user?.photo_url || '';
  };

  console.log('[RESPONSE CARD]', infoUsers);

  return (
    <Box
      sx={{
        borderLeft: `2px solid ${theme.primary}`,
        borderRadius: 2,
        pt: 2,
        px: 1,
        mb: 1,
        backgroundColor: theme.background.primary,
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="start">
        <Stack direction="column" spacing={1} width="70%">
          <Tooltip title={value} placement="bottom-start" arrow>
            <Typography
              variant="body2"
              fontSize={14}
              sx={{
                mb: 2,
                lineHeight: 1.4,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'help',
              }}
            >
              {value}
            </Typography>
          </Tooltip>

          {hasTopChoiceBadge ? (
            <Box
              sx={{
                top: 8,
                right: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <AiFillFire size={12} />
              <Typography variant="caption" fontSize={12} fontWeight={700}>
                Mais escolhido
              </Typography>
            </Box>
          ) : (
            <Box height={12} />
          )}
        </Stack>

        <Stack
          direction="column"
          justifyContent="space-between"
          alignItems="end"
          width="30%"
          mt={-1}
        >
          {users.length > 0 && (
            <Box display="flex" alignItems="start" gap={1}>
              <AvatarGroup max={maxUsers} total={users.length}>
                {users.slice(0, maxUsers).map((userId, index) => (
                  <Avatar
                    key={`${userId}_${index}`}
                    src={getUserPhotoUrl(userId)}
                    sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                  >
                    {userId.slice(0, 2).toUpperCase()}
                  </Avatar>
                ))}
              </AvatarGroup>

              {users.length > maxUsers && (
                <Chip
                  label={`+${users.length - maxUsers}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 20 }}
                />
              )}
            </Box>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width={90}
            mt={1}
          >
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <IoPieChartSharp size={13} />
              <Typography variant="body2" fontSize={12} fontWeight={700}>
                {percentage.toFixed(0)}%
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.25}>
              <FaVoteYea size={14} />
              <Typography variant="body2" fontSize={12} fontWeight={700}>
                {total_users}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ResponseCard;
